import type React from 'react'
import {TabsNavigation} from 'pepsico-ds'
import type {ITabsNavigationProps} from 'pepsico-ds'

interface TabsNavigationWrapperProps extends Omit<ITabsNavigationProps, 'onUpdate' | 'selectedIndex' | 'isDark'> {
  /**
   * Function to handle tab change events. Works as an alias for onUpdate.
   */
  handleSetActiveTab?: (index: number) => void
  /**
   * The index of the currently active tab. Works as an alias for selectedIndex.
   */
  activeTabIndex?: number
  /**
   * Standard onUpdate function from ITabsNavigationProps
   */
  onUpdate?: (index: number) => void
  /**
   * Standard selectedIndex from ITabsNavigationProps
   */
  selectedIndex?: number
}

/**
 * TabsNavigationWrapper - A wrapper component for the pepsico-ds TabsNavigation component.
 * It provides tab navigation functionality with different orientations and styles.
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - The tabs to display
 * @param {number} props.activeTabIndex - The index of the currently active tab (alias for selectedIndex)
 * @param {Function} props.handleSetActiveTab - Function to handle tab change events (alias for onUpdate)
 * @param {string} props.orientation - Tab orientation ('horizontal' or 'vertical')
 * @param {string} props.type - Tab style type ('line', 'pill', etc.)
 * @param {string} props.className - Additional CSS classes
 */
const TabsNavigationWrapper: React.FC<TabsNavigationWrapperProps> = ({
  // Basic tab configuration
  children,
  selectedIndex,
  onUpdate,
  activeTabIndex,
  handleSetActiveTab,

  // Visual styling
  disabled,
  orientation,
  type,

  // Responsive behavior
  maxVisibleTabs,

  // HTML div attributes
  className,
  style,
  ...rest
}) => {
  // Support both naming conventions (activeTabIndex and selectedIndex)
  const finalSelectedIndex = activeTabIndex !== undefined ? activeTabIndex : selectedIndex
  const finalOnUpdate = handleSetActiveTab ?? onUpdate

  // Combine the provided className with our custom full-width class
  const finalClassName = className ? `${className} w-full select-none` : 'w-full select-none'

  return (
    <TabsNavigation
      // Basic tab configuration
      selectedIndex={finalSelectedIndex}
      onUpdate={finalOnUpdate}
      // Visual styling
      disabled={disabled}
      orientation={orientation}
      type={type}
      // Responsive behavior
      maxVisibleTabs={maxVisibleTabs}
      // HTML div attributes
      className={finalClassName}
      style={style}
      {...rest}
    >
      {children}
    </TabsNavigation>
  )
}

export default TabsNavigationWrapper
